import { PNApiOperations } from "@/api/operations"
import { isNetworkConnectivityError } from "@/api/network-behavior"
import { parseMaintenanceModeAPIResponse } from "@/store/maintenance-mode"
import { AxiosError, AxiosHeaderValue, AxiosHeaders, isAxiosError } from "axios"
import * as Sentry from "@sentry/react"
import { isCorruptedFileError } from "@/utils/error"
import { getTelemetryRequestPath } from "./request-path"

const isWorker = typeof WorkerGlobalScope !== "undefined" && self instanceof WorkerGlobalScope
const MISSING_HEADER_VALUE = "missing"
const TRACKED_RESPONSE_HEADERS = [
	"x-request-id", // Backend/app request id (example: 54f5e722-851d-4313-9084-58db4b9e3311)
	"x-correlation-id", // Cross-service correlation id (example: edec15f17d8348609c2611d1f8760d7d)
	"cf-ray", // Cloudflare edge request id + POP suffix (example: 9e4a044cdd4c6ad1-SJC)
	"x-amz-cf-id", // CloudFront request id when traffic passes CloudFront (example: yF9dXfoct1tRqARVt8-cs2wgPL9UpUohp7xkm5hK7accQXd-QlYsmQ==)
	"server", // Immediate responder identity (example: cloudflare / istio-envoy)
	"via", // Proxy/CDN hop chain (example: 1.1 1c4d129deb2211304c934a250596192c.cloudfront.net (CloudFront))
] as const

type TrackedResponseHeader = (typeof TRACKED_RESPONSE_HEADERS)[number]
type TrackedResponseHeaderContext = Record<string, string>
type AxiosResponseHeaders = NonNullable<AxiosError["response"]>["headers"]
type AxiosSentryContext = {
	url: string
	method: string
	status: number | string
	response_headers: TrackedResponseHeaderContext
}
type NetworkConnectivityContext = {
	online: boolean
	effective_type: string
}
type NavigatorWithConnection = Navigator & {
	connection?: {
		effectiveType?: string
	}
}

const hasNavigatorConnection = (value: Navigator): value is NavigatorWithConnection =>
	"connection" in value

const normalizeHeaderValue = (value: AxiosHeaderValue | undefined): string | null => {
	if (typeof value === "string") {
		return value.length > 0 ? value : null
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return String(value)
	}
	if (Array.isArray(value)) {
		const joined = value.map(String).filter(Boolean).join(", ")
		return joined.length > 0 ? joined : null
	}
	return null
}

const readHeaderValue = (
	headers: AxiosResponseHeaders | undefined,
	headerName: TrackedResponseHeader,
): string => {
	if (!headers) {
		return MISSING_HEADER_VALUE
	}

	const value =
		headers instanceof AxiosHeaders
			? headers.get(headerName)
			: Object.entries(headers).find(([key]) => key.toLowerCase() === headerName)?.[1]
	return normalizeHeaderValue(value) ?? MISSING_HEADER_VALUE
}

const buildResponseHeaderContext = (
	headers: AxiosResponseHeaders | undefined,
): TrackedResponseHeaderContext =>
	Object.fromEntries(
		TRACKED_RESPONSE_HEADERS.map((headerName) => [
			headerName,
			readHeaderValue(headers, headerName),
		]),
	)

const buildAxiosSentryContext = (error: unknown): AxiosSentryContext | null => {
	try {
		if (!isAxiosError(error)) {
			return null
		}

		const method = error.config?.method
		return {
			url: getTelemetryRequestPath(error.config?.url) ?? MISSING_HEADER_VALUE,
			method: typeof method === "string" ? method.toUpperCase() : MISSING_HEADER_VALUE,
			status: error.response?.status ?? MISSING_HEADER_VALUE,
			response_headers: buildResponseHeaderContext(error.response?.headers),
		}
	} catch {
		return null
	}
}

export const isMaintenanceModeAPIError = (error: unknown): boolean => {
	try {
		return (
			isAxiosError(error) &&
			error.response?.status === 503 &&
			parseMaintenanceModeAPIResponse(error.response.data) !== null
		)
	} catch {
		return false
	}
}

const readObjectProperty = (value: object, key: string): unknown => {
	try {
		return Reflect.get(value, key)
	} catch {
		return undefined
	}
}

const getNetworkConnectivityContext = (): NetworkConnectivityContext | null => {
	try {
		if (typeof navigator === "undefined") {
			return null
		}

		const effectiveType = hasNavigatorConnection(navigator)
			? navigator.connection?.effectiveType
			: undefined

		return {
			online: navigator.onLine,
			effective_type: effectiveType ?? "unknown",
		}
	} catch {
		return null
	}
}

const isLikelyNetworkErrorForConnectivityContext = (error: unknown): boolean => {
	if (isNetworkConnectivityError(error)) {
		return true
	}

	if (!(error instanceof Error)) {
		return false
	}

	if (/failed to load/i.test(error.message)) {
		return true
	}

	return isNetworkConnectivityError(readObjectProperty(error, "cause"))
}

function isSafari(): boolean {
	if (typeof navigator === "undefined") return false
	return (
		/Safari/.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|Edg/.test(navigator.userAgent)
	)
}

export function sentryInit() {
	if (!import.meta.env.VITE_SENTRY_DSN || !import.meta.env.VITE_SENTRY_ENV) {
		return
	}

	Sentry.init({
		enabled: import.meta.env.PROD,
		environment: import.meta.env.VITE_SENTRY_ENV,
		release: `${APP_VERSION}+${REVISION}`,
		dsn: import.meta.env.VITE_SENTRY_DSN,
		ignoreErrors: [
			// Refresh failures are captured once at the shared refresh attempt with reason tags.
			/^Pixieset access token refresh failed$/,
			// Ignore errors due to PWA deprecation
			/Failed to register a ServiceWorker/i,
			// Ignore operations interrupted by navigation events and network issues
			/Operation has been aborted/i,
			// Ignore offline/network disconnects
			/ERR_INTERNET_DISCONNECTED/i,
			// Ignore CSS preload warnings since they don't affect functionality
			/Unable to preload CSS/i,
			// Ignore dynamic import failures which are often due to navigation interrupts
			/Failed to fetch dynamically imported module/i,
			// Ignore user-cancelled directory picker operations
			/The user aborted a request/i,
			// Ignore invalid directory issues
			/A requested file or directory could not be found at the time an operation was processed/i,
			// Ignore user initiated cancellations
			/ERR_ABORTED (-3)/i,
			// Ignore HTTP 415 Unsupported Media Type errors (client-side content-type issues)
			/Request failed with status code 415/i,
			// Ignore HTTP 403/409 errors from API calls which are expected in normal control flow
			/Request failed with status code 403/i,
			/Request failed with status code 409/i,
			// Ignore network change errors (user switching networks)
			/net::ERR_NETWORK_CHANGED/i,
			// Ignore timeout errors due to user inactivity
			/Transaction timed out due to inactivity/i,
			// Ignore out of space errors
			/FILE_ERROR_NO_SPACE/i,
			/Failed to write blobs/i,
			// Non-actionable abort errors
			/AbortError/i,
		],

		beforeSendTransaction(event) {
			const n1Pattern = /\/api\/styles\/categories\/[^/]+\/styles\b/
			if (event.spans) {
				const matching = event.spans.filter(
					(span) => span.op === "http.client" && n1Pattern.test(span.description ?? ""),
				)
				if (matching.length > 1) {
					const keep = new Set(matching.slice(0, 1).map((s) => s.span_id))
					event.spans = event.spans.filter(
						(span) => !n1Pattern.test(span.description ?? "") || keep.has(span.span_id),
					)
				}
			}
			return event
		},

		beforeSend(event, hint) {
			const originalException = hint?.originalException
			if (isMaintenanceModeAPIError(originalException)) {
				return null
			}

			// Don't send corrupted file errors to Sentry
			if (event.exception?.values?.some((value) => isCorruptedFileError(value.value))) {
				return null
			}

			const axiosExtra = buildAxiosSentryContext(hint?.originalException)
			if (axiosExtra) {
				event.extra = {
					...event.extra,
					axios: axiosExtra,
				}
			}

			const error = originalException instanceof Error ? originalException : undefined

			if (isLikelyNetworkErrorForConnectivityContext(originalException)) {
				const connectivity = getNetworkConnectivityContext()
				if (connectivity) {
					event.extra = {
						...event.extra,
						network_connectivity: connectivity,
					}
				}
			}

			if (
				isSafari() &&
				(error?.name === "DatabaseClosedError" ||
					error?.message?.includes("Connection to Indexed Database server lost"))
			) {
				event.level = "warning"
				event.tags = {
					...event.tags,
					safari_issue: "true", // Use to filter Safari-specific issues in Sentry dashboard
				}
			}

			return event
		},

		integrations: [
			Sentry.linkedErrorsIntegration(),
			Sentry.browserTracingIntegration({
				instrumentPageLoad: !isWorker,
				instrumentNavigation: !isWorker,
			}),
			Sentry.replayIntegration({
				blockAllMedia: false,
				maskAllInputs: false,
				maskAllText: false,
				networkDetailAllowUrls: [
					PNApiOperations.basePaths.projects,
					PNApiOperations.basePaths.presets,
				],
			}),
		],

		sampleRate: 0.5,
		tracesSampleRate: 0.5,

		tracePropagationTargets: [
			/^https:\/\/editor\.pixieset\.com/,
			/^https:\/\/editor\.nextshutter\.com/,
		],

		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 1,
	})
}

export { Sentry }
